import React from "react";
import {Button, ButtonGroup, Col, CustomInput, FormGroup, Input, Label, Progress, Row} from "reactstrap";
import {inject, observer} from "mobx-react";
import configuration from "../../../../configuration/configuration";
import ModalAkitatek from "../../../../components/Akitatek/Modal/Modal";
import WidgetSearch from "../../../../components/Akitatek/Form/Search/WidgetSearch";
import PickAsset from "../../Asset/PickAsset";
import CarteRepliable from "./CarteRepliable";
import Diffusion from "../../../screen/publicite/Diffusion";
import {
    ANIMATIONS, aEnvoyer, CADRES, deplacer, diapositivesDe, diapositiveVide, LIMITES, nouvelId, normaliser, POSITIONS, TAILLES,
    texteVide, TYPES_FOND,
} from "../../../screen/publicite/diaporama";
import {FONDS_ANIMES} from "../../../screen/publicite/fondsAnimes";
import {ECRAN} from "../../../screen/ClientModeComponent";
import {bloqueurDePubActif, MESSAGE_BLOQUEUR} from "../../../../util/bloqueurPub";

/** Échelle de l'aperçu : l'écran de la boutique (1 080 px de large) réduit à environ 300 px. */
const ECHELLE = 0.28;
const APERCU = {"--u": `${ECRAN.largeur / 100}px`, width: ECRAN.largeur, height: ECRAN.hauteur};

const options = liste => liste.map(o => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>);

/** Résumé d'une diapositive pour la liste : son premier texte, ou son fond. */
const resume = d => {
    const texte = d.textes.find(t => t.texte.trim());
    if (texte) return texte.texte.split("\n")[0].slice(0, 40);
    const type = TYPES_FOND.find(t => t.valeur === d.fond.type);
    return d.fond.nom || (type ? type.libelle : "");
};

/**
 * Champ numérique qui n'applique la valeur qu'en quittant le champ (ou avec Entrée) : on peut taper « 15 » sans que
 * « 1 » soit d'abord ramené dans les bornes. Une valeur vide donne null (par exemple « jusqu'à la fin »).
 */
function NombreInput({valeur, onValeur, ...props}) {
    const [texte, setTexte] = React.useState(valeur === null ? "" : String(valeur));
    React.useEffect(() => setTexte(valeur === null ? "" : String(valeur)), [valeur]);
    const appliquer = () => onValeur(texte.trim() === "" ? null : texte.replace(",", "."));
    return <Input type="number" {...props} value={texte} onChange={e => setTexte(e.target.value)} onBlur={appliquer}
                  onKeyDown={e => e.key === "Enter" && appliquer()}/>;
}

const ICONES = {couleur: "fa-square", anime: "fa-magic", image: "fa-picture-o", video: "fa-film"};

/**
 * Carte « Publicité » : éditeur du diaporama du mode Publicité de l'écran client.
 * Chaque diapositive a un fond (couleur, fond animé, image de la galerie ou vidéo) et des textes superposés, chacun avec
 * son heure d'apparition et de disparition et son animation. Tout se règle avec un aperçu en direct ; rien n'est envoyé à
 * l'écran de la boutique avant « Enregistrer ».
 */
const EditeurPublicite = (inject("rootStore")(observer(class EditeurPublicite extends React.Component {

    state = {
        brouillon: null,      // diapositives en cours de modification (null : celles enregistrées)
        choisie: 0,           // index de la diapositive ouverte
        toutLeDiaporama: false,
        galerie: false,
        videos: null,         // vidéos de la galerie, chargées à la demande
        envoi: null,          // pourcentage du téléversement d'une vidéo en cours
        enregistrement: false,
        rejouer: 0,           // change pour rejouer l'aperçu
        bloqueur: false,      // un bloqueur de publicité empêche les appels /api/publicite
    };

    componentDidMount() {
        this.verifierBloqueur();
    }

    /** Vrai (et l'avertissement s'affiche) si un bloqueur de publicité bloque les adresses « publicite ». */
    async verifierBloqueur() {
        const bloqueur = await bloqueurDePubActif(configuration.API);
        if (this.state.bloqueur !== bloqueur) this.setState({bloqueur});
        return bloqueur;
    }

    get auth() {
        return this.props.rootStore.authStore;
    }

    get notifications() {
        return this.props.rootStore.notificationStore;
    }

    diapositives() {
        return this.state.brouillon || diapositivesDe(this.auth.publicite);
    }

    modifier(diapositives, choisie = this.state.choisie) {
        this.setState({brouillon: diapositives, choisie: Math.max(0, Math.min(choisie, diapositives.length - 1))});
    }

    /** Modifie la diapositive ouverte (un objet de champs, ou une fonction de la diapositive). */
    changerDiapositive(changement) {
        const liste = this.diapositives().slice();
        const actuelle = liste[this.state.choisie];
        liste[this.state.choisie] = normaliser({...actuelle, ...(typeof changement === "function" ? changement(actuelle) : changement)});
        this.modifier(liste);
    }

    changerFond(champs) {
        this.changerDiapositive(d => ({fond: {...d.fond, ...champs}}));
    }

    changerTexte(index, champs) {
        this.changerDiapositive(d => ({textes: d.textes.map((t, i) => (i === index ? {...t, ...champs} : t))}));
    }

    ajouterDiapositive() {
        const liste = this.diapositives();
        if (liste.length >= LIMITES.diapositives) return;
        this.modifier([...liste, diapositiveVide()], liste.length);
    }

    dupliquer(index) {
        const liste = this.diapositives().slice();
        if (liste.length >= LIMITES.diapositives) return;
        const copie = {...liste[index], id: nouvelId(), textes: liste[index].textes.map(t => ({...t, id: nouvelId()}))};
        liste.splice(index + 1, 0, copie);
        this.modifier(liste, index + 1);
    }

    supprimer(index) {
        if (!window.confirm("Supprimer cette diapositive ?")) return;
        const liste = this.diapositives().filter((_, i) => i !== index);
        this.modifier(liste, Math.min(this.state.choisie, liste.length - 1));
    }

    ajouterTexte() {
        this.changerDiapositive(d => {
            if (d.textes.length >= LIMITES.textes) return {};
            const dernier = d.textes[d.textes.length - 1];
            const debut = dernier ? Math.min(d.duree - 1, dernier.debut + 1.5) : 0.5;
            return {textes: [...d.textes, {...texteVide(Math.max(0, debut)), position: dernier ? dernier.position : "centre"}]};
        });
    }

    async enregistrer() {
        this.setState({enregistrement: true});
        try {
            await this.auth.enregistrerPublicite(aEnvoyer(this.diapositives()));
            this.setState({brouillon: null});
            this.notifications.setNotification("Publicité enregistrée : l'écran client est à jour", "success");
        } catch (erreur) {
            this.notifications.setNotification(typeof erreur === "string" ? erreur : "Publicité non enregistrée", "error");
        } finally {
            this.setState({enregistrement: false});
        }
    }

    // ---- images et vidéos ----

    ouvrirGalerie() {
        this.props.rootStore.imageStore.pickAsset = null;
        this.setState({galerie: true});
    }

    imageChoisie = () => {
        const imageStore = this.props.rootStore.imageStore;
        const asset = imageStore.pickAsset;
        imageStore.pickAsset = null;
        this.setState({galerie: false});
        if (asset && asset.id) this.changerFond({type: "image", asset_id: asset.id, url: "/images/" + asset.filename, nom: asset.nom});
    };

    async chargerVideos() {
        try {
            const reponse = await this.auth.api.auth.getVideosPublicite();
            this.setState({videos: reponse.data || []});
        } catch (e) {
            this.setState({videos: []});
            this.notifications.setNotification(await this.verifierBloqueur() ? MESSAGE_BLOQUEUR : "Liste des vidéos indisponible", "error");
        }
    }

    async envoyerVideo(fichier) {
        if (!fichier) return;
        this.setState({envoi: 0});
        try {
            const reponse = await this.auth.api.auth.ajouterVideoPublicite(fichier, envoi => this.setState({envoi}));
            const video = reponse.data;
            this.setState(({videos}) => ({videos: [video, ...(videos || [])]}));
            this.changerFond({type: "video", asset_id: video.id, url: video.url, nom: video.nom});
            this.notifications.setNotification("Vidéo ajoutée", "success");
        } catch (erreur) {
            const message = await this.verifierBloqueur() ? MESSAGE_BLOQUEUR : (typeof erreur === "string" ? erreur : "Vidéo non envoyée");
            this.notifications.setNotification(message, "error");
        } finally {
            this.setState({envoi: null});
        }
    }

    // ---- affichage ----

    fondProps = id => ({
        images: (this.auth.fondImages[id] || []).map(image => configuration.API + image.url),
        reglages: this.auth.fondReglages[id],
        horaires: this.auth.fondHoraires,
    });

    apercu(diapositives, d) {
        const {toutLeDiaporama, choisie, rejouer} = this.state;
        const liste = toutLeDiaporama ? diapositives : d ? [{...d, actif: true}] : [];
        return (
            <>
                <div style={{width: ECRAN.largeur * ECHELLE, height: ECRAN.hauteur * ECHELLE, overflow: "hidden", borderRadius: ".375rem", boxShadow: "0 2px 10px rgba(0,0,0,.35)"}}>
                    <div style={{width: ECRAN.largeur, transform: `scale(${ECHELLE})`, transformOrigin: "top left"}}>
                        <Diffusion key={(toutLeDiaporama ? "tout" : "une-" + choisie) + "-" + rejouer} diapositives={liste}
                                   base={configuration.API} fondProps={this.fondProps} style={APERCU}/>
                    </div>
                </div>
                <div className="mt-2" style={{width: ECRAN.largeur * ECHELLE}}>
                    <Button size="sm" color="secondary" className="mr-2" onClick={() => this.setState(({rejouer}) => ({rejouer: rejouer + 1}))}>
                        <i className="fa fa-repeat mr-1"/>Rejouer
                    </Button>
                    <CustomInput type="switch" id="publicite-tout" inline label="Tout le diaporama" checked={toutLeDiaporama}
                                 onChange={e => this.setState({toutLeDiaporama: e.target.checked})}/>
                </div>
                {d && !toutLeDiaporama && this.frise(d)}
            </>
        );
    }

    /** Frise des textes : quand chacun apparaît et disparaît pendant la diapositive. */
    frise(d) {
        if (!d.textes.length) return null;
        const duree = d.duree;
        return (
            <div className="mt-3" style={{width: ECRAN.largeur * ECHELLE}}>
                <small className="text-muted d-block mb-1">Ordre d'apparition des textes ({duree} s)</small>
                {d.textes.map((t, i) => {
                    const debut = Math.min(t.debut, duree), fin = t.fin === null ? duree : Math.min(t.fin, duree);
                    return (
                        <div key={t.id} title={`${t.debut} s → ${t.fin === null ? "fin" : t.fin + " s"}`}
                             style={{position: "relative", height: 16, background: "#eef0f3", borderRadius: 3, marginBottom: 3}}>
                            <div style={{position: "absolute", left: `${debut / duree * 100}%`, width: `${Math.max(2, (fin - debut) / duree * 100)}%`,
                                top: 0, bottom: 0, background: "#20a8d8", borderRadius: 3, color: "#fff", fontSize: 10, lineHeight: "16px",
                                paddingLeft: 4, overflow: "hidden", whiteSpace: "nowrap"}}>
                                {i + 1}. {t.texte.split("\n")[0]}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    liste(diapositives) {
        const {choisie} = this.state;
        return (
            <div className="mb-3">
                {diapositives.map((d, i) => (
                    <div key={d.id} onClick={() => this.setState({choisie: i})}
                         className={"d-flex align-items-center p-2 mb-1 rounded " + (i === choisie ? "bg-primary text-white" : "bg-light")}
                         style={{cursor: "pointer", opacity: d.actif ? 1 : 0.55}}>
                        <strong className="mr-2" style={{minWidth: 20}}>{i + 1}</strong>
                        <i className={`fa ${ICONES[d.fond.type]} mr-2`} aria-hidden="true"/>
                        <span className="flex-grow-1 text-truncate">{resume(d) || <em>Sans texte</em>}</span>
                        <small className="mx-2 text-nowrap">{d.fond.type === "video" && d.video_entiere ? "vidéo entière" : d.duree + " s"}</small>
                        <ButtonGroup size="sm" onClick={e => e.stopPropagation()}>
                            <Button color="light" title="Monter" disabled={i === 0} onClick={() => this.modifier(deplacer(diapositives, i, -1), i === choisie ? i - 1 : choisie)}>↑</Button>
                            <Button color="light" title="Descendre" disabled={i === diapositives.length - 1} onClick={() => this.modifier(deplacer(diapositives, i, 1), i === choisie ? i + 1 : choisie)}>↓</Button>
                            <Button color="light" title="Dupliquer" onClick={() => this.dupliquer(i)}><i className="fa fa-clone"/></Button>
                            <Button color="light" title="Supprimer" onClick={() => this.supprimer(i)}><i className="fa fa-trash text-danger"/></Button>
                        </ButtonGroup>
                    </div>
                ))}
                <Button size="sm" color="success" className="mt-1" disabled={diapositives.length >= LIMITES.diapositives}
                        onClick={() => this.ajouterDiapositive()}>
                    <i className="fa fa-plus mr-1"/>Ajouter une diapositive
                </Button>
            </div>
        );
    }

    choixFond(d) {
        const {fond} = d;
        const {videos, envoi} = this.state;
        return (
            <>
                <Row form>
                    <Col sm="6">
                        <FormGroup>
                            <Label for="pub-fond">Fond</Label>
                            <Input type="select" id="pub-fond" value={fond.type}
                                   onChange={e => {
                                       const type = e.target.value;
                                       this.changerFond(type === "anime" && !fond.anime ? {type, anime: FONDS_ANIMES[0].valeur} : {type});
                                       if (type === "video" && !videos) this.chargerVideos();
                                   }}>
                                {options(TYPES_FOND)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col sm="6">
                        <FormGroup>
                            <Label for="pub-couleur">{fond.type === "couleur" ? "Couleur" : "Couleur de secours"}</Label>
                            <Input type="color" id="pub-couleur" value={fond.couleur} onChange={e => this.changerFond({couleur: e.target.value})}
                                   title="Affichée sous l'image ou la vidéo, et à sa place si elle a été supprimée de la galerie"/>
                        </FormGroup>
                    </Col>
                </Row>

                {fond.type === "anime" && (
                    <FormGroup>
                        <Label for="pub-anime">Fond animé</Label>
                        <Input type="select" id="pub-anime" value={fond.anime || ""} onChange={e => this.changerFond({anime: e.target.value})}>
                            {options(FONDS_ANIMES)}
                        </Input>
                        <small className="text-muted">Les fonds du bandeau, en plein écran, avec leurs réglages et images de la carte « Fond du bandeau ».</small>
                    </FormGroup>
                )}

                {fond.type === "image" && (
                    <FormGroup className="d-flex align-items-center">
                        {fond.url
                            ? <img src={configuration.API + fond.url} alt={fond.nom || ""} height={56} className="mr-3" style={{maxWidth: 140, objectFit: "contain", borderRadius: 4}}/>
                            : <span className="text-muted mr-3">Aucune image</span>}
                        <Button size="sm" color="secondary" onClick={() => this.ouvrirGalerie()}>{fond.url ? "Changer l'image" : "Choisir dans la galerie"}</Button>
                    </FormGroup>
                )}

                {fond.type === "video" && (
                    <FormGroup>
                        <Label for="pub-video">Vidéo (sans le son : l'écran de la boutique joue les vidéos muettes)</Label>
                        <Input type="select" id="pub-video" value={fond.asset_id || ""} onFocus={() => !videos && this.chargerVideos()}
                               onChange={e => {
                                   const video = (videos || []).find(v => String(v.id) === e.target.value);
                                   if (video) this.changerFond({asset_id: video.id, url: video.url, nom: video.nom});
                               }}>
                            <option value="">{videos ? (videos.length ? "Choisir une vidéo…" : "Aucune vidéo : envoyez-en une") : "Chargement…"}</option>
                            {fond.asset_id && !(videos || []).some(v => v.id === fond.asset_id) && <option value={fond.asset_id}>{fond.nom || "Vidéo actuelle"}</option>}
                            {(videos || []).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
                        </Input>
                        <div className="mt-2">
                            {envoi === null ? (
                                <Label className="btn btn-sm btn-secondary mb-0">
                                    <i className="fa fa-upload mr-1"/>Envoyer une vidéo (MP4 ou WebM, 200 Mo au plus)
                                    <input type="file" accept="video/mp4,video/webm" hidden
                                           onChange={e => { this.envoyerVideo(e.target.files[0]); e.target.value = ""; }}/>
                                </Label>
                            ) : <Progress value={envoi}>{envoi} %</Progress>}
                        </div>
                        <CustomInput type="checkbox" id="pub-video-entiere" className="mt-2" checked={d.video_entiere}
                                     label="Rester jusqu'à la fin de la vidéo (sinon elle tourne en boucle pendant la durée choisie)"
                                     onChange={e => this.changerDiapositive({video_entiere: e.target.checked})}/>
                    </FormGroup>
                )}

                <Row form>
                    <Col sm="6">
                        <FormGroup>
                            <Label for="pub-duree">Durée de la diapositive</Label>
                            <NombreInput id="pub-duree" min={LIMITES.duree.min} max={LIMITES.duree.max} valeur={d.duree}
                                         disabled={fond.type === "video" && d.video_entiere}
                                         onValeur={duree => this.changerDiapositive({duree})}/>
                            <small className="text-muted">en secondes ({LIMITES.duree.min} à {LIMITES.duree.max})</small>
                        </FormGroup>
                    </Col>
                    <Col sm="6">
                        <FormGroup>
                            <Label for="pub-assombrir">Assombrir le fond : {d.assombrir} %</Label>
                            <CustomInput type="range" id="pub-assombrir" min={0} max={LIMITES.assombrir.max} step={5} value={d.assombrir}
                                         onChange={e => this.changerDiapositive({assombrir: e.target.value})}/>
                            <small className="text-muted">pour que les textes se lisent mieux</small>
                        </FormGroup>
                    </Col>
                </Row>
                <CustomInput type="switch" id="pub-actif" className="mb-3" checked={d.actif} label="Diapositive affichée (décocher pour la mettre de côté sans la supprimer)"
                             onChange={e => this.changerDiapositive({actif: e.target.checked})}/>
            </>
        );
    }

    textes(d) {
        return (
            <>
                <h6 className="mt-2">Textes <small className="text-muted">(ils apparaissent l'un après l'autre, à l'heure choisie)</small></h6>
                {d.textes.map((t, i) => (
                    <div key={t.id} className="border rounded p-2 mb-2">
                        <div className="d-flex align-items-start">
                            <strong className="mr-2 mt-1">{i + 1}</strong>
                            <Input type="textarea" rows={2} maxLength={LIMITES.texte} value={t.texte} placeholder="Votre texte (Entrée pour aller à la ligne)"
                                   onChange={e => this.changerTexte(i, {texte: e.target.value})}/>
                            <ButtonGroup size="sm" className="ml-2" vertical>
                                <Button color="light" title="Monter" disabled={i === 0}
                                        onClick={() => this.changerDiapositive(x => ({textes: deplacer(x.textes, i, -1)}))}>↑</Button>
                                <Button color="light" title="Descendre" disabled={i === d.textes.length - 1}
                                        onClick={() => this.changerDiapositive(x => ({textes: deplacer(x.textes, i, 1)}))}>↓</Button>
                                <Button color="light" title="Supprimer ce texte"
                                        onClick={() => this.changerDiapositive(x => ({textes: x.textes.filter((_, j) => j !== i)}))}><i className="fa fa-trash text-danger"/></Button>
                            </ButtonGroup>
                        </div>
                        <Row form className="mt-2">
                            <Col xs="6" md="3">
                                <Label className="small mb-0">Apparaît à (s)</Label>
                                <NombreInput bsSize="sm" min={0} max={LIMITES.duree.max} step={0.5} valeur={t.debut}
                                             onValeur={debut => this.changerTexte(i, {debut: debut === null ? 0 : debut})}/>
                            </Col>
                            <Col xs="6" md="3">
                                <Label className="small mb-0">Disparaît à (s)</Label>
                                <NombreInput bsSize="sm" min={0} max={LIMITES.duree.max} step={0.5} valeur={t.fin}
                                             placeholder="à la fin" onValeur={fin => this.changerTexte(i, {fin})}/>
                            </Col>
                            <Col xs="6" md="3">
                                <Label className="small mb-0">Animation</Label>
                                <Input bsSize="sm" type="select" value={t.animation} onChange={e => this.changerTexte(i, {animation: e.target.value})}>{options(ANIMATIONS)}</Input>
                            </Col>
                            <Col xs="6" md="3">
                                <Label className="small mb-0">Position</Label>
                                <Input bsSize="sm" type="select" value={t.position} onChange={e => this.changerTexte(i, {position: e.target.value})}>{options(POSITIONS)}</Input>
                            </Col>
                            <Col xs="6" md="3" className="mt-1">
                                <Label className="small mb-0">Taille</Label>
                                <Input bsSize="sm" type="select" value={t.taille} onChange={e => this.changerTexte(i, {taille: e.target.value})}>{options(TAILLES)}</Input>
                            </Col>
                            <Col xs="6" md="3" className="mt-1">
                                <Label className="small mb-0">Couleur</Label>
                                <Input bsSize="sm" type="color" value={t.couleur} onChange={e => this.changerTexte(i, {couleur: e.target.value})}/>
                            </Col>
                            <Col xs="12" md="6" className="mt-1">
                                <Label className="small mb-0">Cadre</Label>
                                <Input bsSize="sm" type="select" value={t.cadre} onChange={e => this.changerTexte(i, {cadre: e.target.value})}>{options(CADRES)}</Input>
                            </Col>
                        </Row>
                    </div>
                ))}
                <Button size="sm" color="secondary" disabled={d.textes.length >= LIMITES.textes} onClick={() => this.ajouterTexte()}>
                    <i className="fa fa-plus mr-1"/>Ajouter un texte
                </Button>
            </>
        );
    }

    render() {
        const diapositives = this.diapositives();
        const d = diapositives[this.state.choisie] || null;
        const modifie = this.state.brouillon !== null;

        return (
            <CarteRepliable id="publicite" titre="Publicité (diaporama)" className="mb-4">
                <>
                    <p className="text-muted">
                        Ce que l'écran client montre en mode Publicité : des diapositives qui passent l'une après l'autre, chacune avec un fond
                        (couleur, fond animé, image ou vidéo) et des textes qui apparaissent dans l'ordre que vous choisissez.
                    </p>
                    {this.state.bloqueur && (
                        <div className="alert alert-warning" role="alert">
                            <i className="fa fa-exclamation-triangle mr-2"/>{MESSAGE_BLOQUEUR}
                        </div>
                    )}
                    <Row>
                        <Col xs="12" md="auto" className="mb-3">
                            <small className="text-muted d-block mb-1">Aperçu {this.state.toutLeDiaporama ? "du diaporama" : "de la diapositive " + (this.state.choisie + 1)}</small>
                            {this.apercu(diapositives, d)}
                        </Col>
                        <Col xs="12" md>
                            {this.liste(diapositives)}
                            {d ? (
                                <div className="border-top pt-3">
                                    <h5>Diapositive {this.state.choisie + 1}</h5>
                                    {this.choixFond(d)}
                                    {this.textes(d)}
                                </div>
                            ) : <p className="text-muted">Aucune diapositive : l'écran affiche l'accueil d'origine.</p>}
                            <div className="border-top mt-3 pt-3">
                                <Button color="primary" className="mr-2" disabled={!modifie || this.state.enregistrement} onClick={() => this.enregistrer()}>
                                    {this.state.enregistrement ? "Enregistrement…" : "Enregistrer et afficher"}
                                </Button>
                                <Button color="link" disabled={!modifie} onClick={() => this.setState({brouillon: null, choisie: 0})}>
                                    Annuler les modifications
                                </Button>
                                {modifie && <small className="text-warning ml-2">Modifications non enregistrées</small>}
                            </div>
                        </Col>
                    </Row>

                    <ModalAkitatek show={this.state.galerie}
                                   title={<WidgetSearch icon={"fa fa-search bg-primary p-1 font-10xl mr-3 float-left"} col={"col-12"} category={"images"}/>}
                                   children={<PickAsset onCallback={this.imageChoisie}/>}
                                   onCallback={() => this.setState({galerie: false})}/>
                </>
            </CarteRepliable>
        );
    }
})));

export default EditeurPublicite;
